import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { SecurityService } from '../security/security.service';

@Injectable({
  providedIn: 'root'
})
export class AvatarCacheService {
  private cache = new Map<string, Observable<SafeUrl | string>>();
  private objectUrlByKey = new Map<string, string>();

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private securityService: SecurityService
  ) {}

  getSafeUrl(url: string | null | undefined, fallback: string = '/images/user/owner.png'): Observable<SafeUrl | string> {
    if (!url) {
      return of(fallback);
    }

    const key = this.normalizeUrl(url);
    const cached = this.cache.get(key);
    if (cached) {
      return cached;
    }

    const token = this.securityService.getToken();
    if (!token) {
      return of(fallback);
    }

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    const request$ = this.http.get(key, { headers, responseType: 'blob' }).pipe(
      map((imageBlob: Blob) => {
        const objectUrl = URL.createObjectURL(imageBlob);
        const previousUrl = this.objectUrlByKey.get(key);
        if (previousUrl && previousUrl !== objectUrl) {
          URL.revokeObjectURL(previousUrl);
        }
        this.objectUrlByKey.set(key, objectUrl);
        return this.sanitizer.bypassSecurityTrustUrl(objectUrl);
      }),
      catchError(() => of(fallback)),
      shareReplay(1)
    );

    this.cache.set(key, request$);
    return request$;
  }

  invalidate(url: string | null | undefined): void {
    if (!url) {
      return;
    }
    const key = this.normalizeUrl(url);
    const objectUrl = this.objectUrlByKey.get(key);
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      this.objectUrlByKey.delete(key);
    }
    this.cache.delete(key);
  }

  clear(): void {
    for (const objectUrl of this.objectUrlByKey.values()) {
      URL.revokeObjectURL(objectUrl);
    }
    this.objectUrlByKey.clear();
    this.cache.clear();
  }

  private normalizeUrl(url: string): string {
    return url.trim();
  }
}
